import { IPropsWithChildren } from '@omaze/app';
import React, { ReactElement, useMemo } from 'react';

import { getVisibilityClass, getValueOrDefault } from '../shared/GridUtils/GridUtils';

import styles from './GridCell.module.css';
import { gridCellDisplayName } from './GridCell.qa';

export enum GRID_CELL_SIZE {
    COLUMN_1 = 1,
    COLUMN_2,
    COLUMN_3,
    COLUMN_4,
    COLUMN_5,
    COLUMN_6,
    COLUMN_7,
    COLUMN_8,
    COLUMN_9,
    COLUMN_10,
    COLUMN_11,
    COLUMN_12,
}

export interface IGridCellProps extends IPropsWithChildren {
    className?: string;
    'data-testid'?: string;

    size?: GRID_CELL_SIZE;
    smSize?: GRID_CELL_SIZE;
    mdSize?: GRID_CELL_SIZE;
    lgSize?: GRID_CELL_SIZE;

    offset?: GRID_CELL_SIZE;
    smOffset?: GRID_CELL_SIZE;
    mdOffset?: GRID_CELL_SIZE;
    lgOffset?: GRID_CELL_SIZE;

    hide?: boolean;
    smHide?: boolean;
    mdHide?: boolean;
    lgHide?: boolean;

    show?: boolean;
    smShow?: boolean;
    mdShow?: boolean;
    lgShow?: boolean;
}

function getSizeClass (sizePrefix: string, sizeType: string, size: GRID_CELL_SIZE | undefined): string {
    if (!size) {
        return '';
    }

    if (sizeType === 'span') {
        return getSpanClass(sizePrefix, size);
    }

    return styles[`${sizePrefix}-${sizeType}-${size}`];
}

function getSpanClass (sizePrefix: string, size: GRID_CELL_SIZE | undefined): string {
    const prefix: { [key: string]: string } = {
        'sm': 'w',
        'md': 'md:w',
        'lg': 'lg:w-lg',
    };

    return `${prefix[sizePrefix]}-${size}-col`;
}

export function OzGridCell (props: IGridCellProps): ReactElement {
    const {
        size,
        smSize,
        mdSize,
        lgSize,

        offset,
        smOffset,
        mdOffset,
        lgOffset,

        hide,
        smHide,
        mdHide,
        lgHide,

        show,
        smShow,
        mdShow,
        lgShow,

        className = '',
        'data-testid': dataTestId,
        children,
    }: IGridCellProps = props;

    const visibilityStyles: { [key: string]: string } = {
        'sm-hide': 'hidden',
        'sm-show': 'block',
        'md-hide': 'md:hidden',
        'md-show': 'md:block',
        'lg-hide': 'lg:hidden',
        'lg-show': 'lg:block',
    };

    const cellClassNames: string = useMemo<string>((): string => {
        const classNames: string[] = [
            className,
            `${styles.host} mx-1`,

            getSizeClass('sm', 'span', getValueOrDefault(smSize, size)),
            getSizeClass('md', 'span', getValueOrDefault(mdSize, size)),
            getSizeClass('lg', 'span', getValueOrDefault(lgSize, size)),

            getSizeClass('sm', 'offset', getValueOrDefault(smOffset, offset)),
            getSizeClass('md', 'offset', getValueOrDefault(mdOffset, offset)),
            getSizeClass('lg', 'offset', getValueOrDefault(lgOffset, offset)),

            getVisibilityClass('sm', 'hide', getValueOrDefault(smHide, hide), visibilityStyles),
            getVisibilityClass('md', 'hide', getValueOrDefault(mdHide, hide), visibilityStyles),
            getVisibilityClass('lg', 'hide', getValueOrDefault(lgHide, hide), visibilityStyles),

            getVisibilityClass('sm', 'show', getValueOrDefault(smShow, show), visibilityStyles),
            getVisibilityClass('md', 'show', getValueOrDefault(mdShow, show), visibilityStyles),
            getVisibilityClass('lg', 'show', getValueOrDefault(lgShow, show), visibilityStyles),
        ];

        return classNames
            .filter((cls: string): boolean => {
                return cls !== '';
            })
            .join(' ');
    }, [
        size,
        smSize,
        mdSize,
        lgSize,

        offset,
        smOffset,
        mdOffset,
        lgOffset,

        hide,
        smHide,
        mdHide,
        lgHide,

        show,
        smShow,
        mdShow,
        lgShow,

        className,
    ]);

    return (
        <div className={cellClassNames} data-testid={dataTestId}>{children}</div>
    );
}

OzGridCell.displayName = gridCellDisplayName;
